/**
 * Hero Gallery Switch
 * 
 * Switches between v1 (SwiperCarousel) and v2 (ListingGallerySection bento) 
 * based on GrowthBook flag.
 * 
 * Uses element orchestrator to control visibility of share/like buttons
 * based on active experiments.
 * 
 * After experiment concludes:
 * 1. Update ../index.tsx to export winner directly
 * 2. Delete this variants/ folder
 */

import { ExperimentalSection } from 'ui/experimental-section';
import { AllowedExperimentKey } from '@tenants/types';
import SwiperCarousel from 'components/swiper-carousel';
import { ComingSoonBadge } from 'ui/badges';
import { ListingGallerySection, GALLERY_VARIANTS } from '../../gallery';
import { useExperimentElementVisibility, ExperimentElement } from 'experiments/element-visibility';
import type { GallerySectionData } from '../../gallery/types';
import type { ComplianceData } from 'ui/compliance';
import type { ComplianceRequirement } from 'types/compliance';
import type Listing from 'data/listing';

export interface HeroGallerySwitchProps {
  listing: Listing;
  gallery: GallerySectionData;
  blur: boolean;
  complianceData?: ComplianceData;
  complianceRequirement: ComplianceRequirement;
  onImageClick?: (index: number) => void;
  onSeeAllClick?: () => void;
  onComplianceClick?: () => void;
  share?: {
    text: string;
    url: string;
    listingId: number;
  };
}

export function HeroGallerySwitch({
  listing,
  gallery,
  blur,
  complianceData,
  complianceRequirement,
  onImageClick,
  onSeeAllClick,
  onComplianceClick,
  share,
}: HeroGallerySwitchProps) {
  // Element orchestration - determines visibility based on experiments and viewport
  const visibility = useExperimentElementVisibility();
  const showCarouselShare = visibility.shouldShow(ExperimentElement.CAROUSEL_SHARE_BUTTON);
  const showCarouselLike = visibility.shouldShow(ExperimentElement.CAROUSEL_LIKE_BUTTON);
  
  const complianceOverlay = complianceData?.shouldShow
    ? { message: complianceData.message, ctaText: complianceData.ctaText, onClick: onComplianceClick }
    : undefined;

  const mapPosition = listing.displayAddress === false ? null : listing.position;

  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.LISTING_HERO}
      control={
        <SwiperCarousel
          listingData={{
            id: listing.id,
            imageUrls: listing.imageUrls,
            imageDescs: listing.imageDescs,
            position: mapPosition,
            onImageClick: onImageClick as ((index: number | 'map') => void) | undefined,
          }}
          imageAltFallback="Listing image"
          complianceRequirement={complianceRequirement}
          onComplianceRequirementClick={onComplianceClick ? () => onComplianceClick() : undefined}
          share={share}
          showFavouriteToggle={false}
          showShareButton={showCarouselShare}
          showLikeButton={showCarouselLike}
          // Match the variation (bento gallery), which shows the Coming Soon overlay via its badgesSlot.
          badgesSlot={<ComingSoonBadge standardStatus={listing.standardStatus} onMarketDate={listing.onMarketDate} providerId={listing.providerId} />}
        />
      }
      variation={
        <ListingGallerySection
          sectionId="hero-gallery"
          data={gallery}
          variant={GALLERY_VARIANTS.BENTO_GRID}
          onImageClick={onImageClick}
          onSeeAllClick={onSeeAllClick}
          blur={blur}
          complianceOverlay={complianceOverlay}
        />
      }
    />
  );
}
